import React, { useEffect, useState } from 'react';
import { PaymentMethodPanel, CreditCardPaymentMethod } from '@atlassian/commerce-payment-methods';
import SectionMessage from '@atlaskit/section-message';
import Lozenge from '@atlaskit/lozenge';
import EmptyState from '@atlaskit/empty-state';
import Button from '@atlaskit/button';
import { getRouteUrl, sortBillingProfiles } from '@css-xp/ppc-utils';
import { routePaths, BillingProfileTableKeys, ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { DynamicResultTable, FormattedAddress, useSidebar, useFeatureToggle } from '@css-xp/base';
import { BillingProfileNameFromId, errorStateProp, genericEmptyStateProp } from '@css-xp/ppc-common';
import { SortDirection } from '@css-xp/ppc-graphtypes';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { BillingProfile } from '@css-xp/graphtypes/src/generated-types-new';
import './remove-payment-method.scss';
import {
  ASSOCIATED_PAYMENT_METHOD,
  ASSOCIATED_PAYMENT_METHOD_REMOVE,
  PAYMENT_METHOD_NOT_ASSOCIATED,
} from '../../../../constants/details';

export const emptyStateBillingProfileProp = {
  header: '',
  description: 'No Billing profiles to show',
  imageUrl: '',
  testId: 'no-results-billingProfile-state',
};

interface Props {
  paymentMethod: CreditCardPaymentMethod;
  billingProfiles: BillingProfile[];
  isLoading: boolean;
  transactionAccountId: string;
  isFromBillingProfile?: boolean;
  isPayPalPaymentMethod?: boolean;
  payPalEmailId?: string;
  error?: any;
}

const getInvoiceGroupStatusLozenge = <Lozenge appearance="removed">DEACTIVATED</Lozenge>;

export const AssociatedBillingProfilesTable = ({
  paymentMethod,
  billingProfiles,
  isLoading,
  transactionAccountId,
  isFromBillingProfile = false,
  isPayPalPaymentMethod = false,
  payPalEmailId,
  error,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const [tableRows, setTableRows] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.ASC);
  const { closeSidebar } = useSidebar();

  let billingProfileItems = [...billingProfiles];

  /* we're using this sorting because the response from api is ASCII sorted, so we need to sort it in a case-insensitive way.
   * This is a temporary solution, and we are only using it here since we are fetching all the billing profiles in one go.
   */
  billingProfileItems = sortBillingProfiles(billingProfileItems, true, false);

  const rows = billingProfileItems.map((billingProfileItem: any) => {
    const billingProfilePageURL = getRouteUrl(routePaths.billingProfileDetails, {
      txaId: transactionAccountId,
      billingProfileId: `${encodeURIComponent(billingProfileItem?.id ?? '')}`,
    });

    return {
      key: billingProfileItem?.id,
      cells: [
        {
          key: BillingProfileTableKeys.NAME,
          content: (
            <>
              {isDesignIteration1Enabled ? (
                <BillingProfileNameFromId
                  id={billingProfileItem?.id}
                  txaId={transactionAccountId}
                  showBillingProfileNumber={true}
                />
              ) : (
                <a
                  data-testid={`billing-profile-link-${billingProfileItem?.id}`}
                  target="_blank"
                  href={billingProfilePageURL}
                  rel="noreferrer"
                >
                  {billingProfileItem?.name}
                </a>
              )}
              {billingProfileItem?.isActive === false && getInvoiceGroupStatusLozenge}
            </>
          ),
        },
        {
          content: (
            <FormattedAddress
              addressLine1={billingProfileItem?.shipTo?.address1}
              addressLine2={billingProfileItem?.shipTo?.address2}
              city={billingProfileItem?.shipTo?.city}
              country={billingProfileItem?.shipTo?.country}
              postcode={billingProfileItem?.shipTo?.postalCode}
              state={billingProfileItem?.shipTo?.state}
              organizationName={billingProfileItem?.shipTo?.name}
            />
          ),
        },
        {
          content: billingProfileItem?.numberOfCloudsites,
        },
        {
          content: billingProfileItem?.numberOfSubscriptions,
        },
      ],
    };
  });

  const columns = {
    cells: [
      {
        key: BillingProfileTableKeys.NAME,
        isSortable: true,
        content: isDesignIteration1Enabled ? 'Billing profile' : 'Billing profile name',
        width: isDesignIteration1Enabled ? 20 : 15,
      },
      {
        content: 'Sold-to address',
        width: 25,
      },
      {
        content: 'Sites',
        width: 10,
      },
      {
        content: 'Subscriptions',
        width: 10,
      },
    ],
  };

  const onSort = () => {
    const newSortedData = [...tableRows].reverse();
    setTableRows(newSortedData);
    setSortOrder(sortOrder === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC);
  };

  const getSubheading = () => {
    if (isLoading) return null;
    if (billingProfiles?.length) {
      return ASSOCIATED_PAYMENT_METHOD_REMOVE;
    }
    return PAYMENT_METHOD_NOT_ASSOCIATED;
  };

  useEffect(() => {
    setTableRows(rows);
  }, [billingProfiles]);

  const renderEmptyOrErrorState = () => {
    if (!isDesignIteration1Enabled) {
      return null;
    }

    if (!billingProfileItems?.length) {
      return error ? <EmptyState {...errorStateProp} /> : <EmptyState {...genericEmptyStateProp} />;
    }

    return null;
  };

  return (
    <>
      <div>
        {!isFromBillingProfile && (
          <div>
            {billingProfiles?.length && !isLoading ? (
              <div className="info-banners">
                <SectionMessage
                  appearance="warning"
                  title="Payment method associated with multiple billing profiles"
                  testId="remove-payment-method-warning"
                >
                  <p>{ASSOCIATED_PAYMENT_METHOD}</p>
                </SectionMessage>
              </div>
            ) : (
              <p className="payment-method-panel-heading" datatest-id="payment-method-panel-heading">
                You are about to remove the following payment method.
              </p>
            )}
            {isPayPalPaymentMethod ? (
              <PaypalPaymentMethod emailID={payPalEmailId} withBorder={true} renderChildrenInline={false} />
            ) : (
              <div className="payment-method-panel-container" data-testid="payment-method-panel-container">
                <PaymentMethodPanel paymentMethod={paymentMethod} />
              </div>
            )}
          </div>
        )}
        <div className="associated-billing-profiles">
          <div className="heading">Associated billing profiles</div>
          {(!isFromBillingProfile && <div className="subheading">{getSubheading()}</div>) || (
            <div className="subheading-profile"></div>
          )}
          {renderEmptyOrErrorState()}
          {(!!billingProfiles?.length || isLoading) && (
            <DynamicResultTable
              testId={'associated-billing-profiles-table'}
              emptyStateProps={emptyStateBillingProfileProp}
              head={columns}
              rows={tableRows}
              isLoading={isLoading}
              isSearchEnabled={false}
              isPaginationEnabled={false}
              boxShadow
              className="associated-billing-profiles-table"
              sortKey={BillingProfileTableKeys.NAME}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          )}
        </div>
        {!isDesignIteration1Enabled && isFromBillingProfile && (
          <Button
            className="associated-close-button"
            appearance="subtle"
            testId="close-button"
            onClick={() => closeSidebar()}
          >
            Close
          </Button>
        )}
      </div>
    </>
  );
};

export default AssociatedBillingProfilesTable;
